<template>
  <div class="guidance-tab act" ref="rootEl">
    <div v-if="loading" class="g-loading">{{ $t('actions.loading') }}</div>

    <template v-else>
      <!-- ===================== Header ===================== -->
      <header class="act-head">
        <div class="act-head-text">
          <h2 class="act-title">{{ $t('actions.title') }}</h2>
          <p class="act-sub">{{ $t('actions.sub') }}</p>
        </div>
        <!-- The one number that proves the loop works — compact, always in view. -->
        <div v-if="earned.has" class="act-earned">
          <span class="act-earned-label"><i class="bi bi-stars"></i> {{ $t('actions.earned.label') }}</span>
          <span class="act-earned-value">{{ eurSigned(earned.measured) }}</span>
          <span class="act-earned-note">
            {{ $t('actions.earned.measured') }}<template v-if="earned.since"> · {{ $t('actions.earned.since', { date: fmtDate(earned.since) }) }}</template>
          </span>
        </div>
      </header>

      <!-- Log your own change — only what each place can actually measure. -->
      <LogChangeForm
        v-if="canEdit && showAddForm"
        :zones="liveZones"
        :today-iso="todayIso"
        :busy="busy"
        @submit="addAction"
        @cancel="showAddForm = false"
      />

      <!-- ===================== Lifecycle tabs =====================
           One stage at a time. The lifecycle IS the navigation: a suggestion flows
           Open → In progress → Concluded, and you only ever work in one of them. -->
      <!-- Tab bar: the lifecycle tabs, and at its right end the page's one action —
           visible from every tab, never competing with the header's € figure. -->
      <div class="stage-bar">
        <nav class="stage-tabs" role="tablist" :aria-label="$t('actions.aria.lifecycle')">
          <template v-for="(st, i) in stages" :key="st.key">
            <span v-if="i" class="stage-arrow" aria-hidden="true"><i class="bi bi-chevron-right"></i></span>
            <button
              type="button"
              role="tab"
              class="stage-tab"
              :class="{ active: stage === st.key }"
              :aria-selected="stage === st.key"
              @click="stage = st.key"
            >
              <span class="stage-count">{{ st.count }}</span>
              <span class="stage-text">
                <span class="stage-label">{{ st.label }}</span>
                <span class="stage-hint">{{ st.hint }}</span>
              </span>
            </button>
          </template>
        </nav>
        <button
          v-if="canEdit"
          type="button"
          class="btn-primary-sm stage-bar-btn"
          :aria-expanded="showAddForm"
          @click="showAddForm = !showAddForm"
        >
          <i class="bi bi-plus-lg"></i> {{ $t('actions.logOwn') }}
        </button>
      </div>

      <!-- ======================== OPEN ======================== -->
      <section v-show="stage === 'open'" class="stage" role="tabpanel" :aria-label="$t('actions.aria.open')">
        <!-- Zone identity 6.7: the € on these suggestions follow a money-settings change tonight. -->
        <ChangeNotice v-if="settingChanged(changesToday.register, 'money')" :compact="true" :text="NOTICES.settings.money" />
        <!-- Forecast heads-up: a one-off tip, not a tracked experiment. -->
        <div v-if="showWeatherHeadsUp" class="notice">
          <i class="bi bi-cloud-sun notice-ic" aria-hidden="true"></i>
          <div class="notice-body">
            <strong>{{ weatherHeadsUp.title }}.</strong> {{ weatherHeadsUp.text }}
          </div>
          <button type="button" class="link-btn" @click="dismissWeatherHeadsUp">{{ $t('actions.noted') }}</button>
        </div>

        <div class="toolbar">
          <div class="filters" role="group" :aria-label="$t('actions.aria.filterKind')">
            <button
              v-for="f in openFilters"
              :key="f.key"
              type="button"
              class="filter"
              :class="['kind-' + f.key, { active: openFilter === f.key }]"
              :disabled="f.key !== 'all' && !f.count"
              :aria-pressed="openFilter === f.key"
              @click="openFilter = f.key"
            >
              <span v-if="f.key !== 'all'" class="dot" aria-hidden="true"></span>
              {{ f.label }} <span class="filter-n">{{ f.count }}</span>
            </button>
          </div>
        </div>

        <p v-if="!openItems.length" class="empty-state">
          {{ $t('actions.empty.open') }}
        </p>

        <ul class="sugg-list">
          <li
            v-for="it in visibleOpen"
            :key="it.s.idea"
            class="sugg"
            :class="['kind-' + it.kind, { flash: flashZone === it.s.zone }]"
            :data-zone="it.s.zone"
          >
            <div class="sugg-main">
              <div class="meta">
                <span class="dot" aria-hidden="true"></span>
                <span class="meta-kind">{{ kindLabel(it.kind) }}</span>
                <span class="meta-sep" aria-hidden="true">·</span>
                <span class="meta-zone">{{ zoneShown(it.s) }}</span>
                <span v-if="it.kind === 'money'" class="meta-lever">{{ leverText(it.s.leverLabel) }}</span>
                <span v-if="it.s.pinned" class="meta-tag">{{ $t('actions.tag.considering') }}</span>
                <Tooltip v-if="execFlagFor(it.s)" :text="execFlagFor(it.s)" placement="top" align="start">
                  <span class="meta-tag tag-warn"><i class="bi bi-arrow-repeat"></i> {{ $t('actions.tag.triedBefore') }}</span>
                </Tooltip>
                <Tooltip v-if="it.s.basis" :text="basisText(it.s.basis)" placement="top" align="start">
                  <span class="meta-tag"><i class="bi bi-info-circle"></i> {{ $t('actions.tag.belowBest') }}</span>
                </Tooltip>
              </div>

              <p class="sugg-action">{{ it.s.action }}</p>

              <button
                v-if="it.s.why"
                type="button"
                class="why-toggle"
                :aria-expanded="!!expanded[it.s.idea]"
                @click="toggleExpanded(it.s.idea)"
              >
                {{ $t('actions.why') }}
                <i class="bi" :class="expanded[it.s.idea] ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
              </button>
              <div v-if="expanded[it.s.idea]" class="sugg-why">
                <p>{{ it.s.why }}</p>
              </div>
            </div>

            <div class="sugg-side">
              <div class="value" :class="{ 'value-muted': !suggValue(it).main }">
                <template v-if="suggValue(it).main">{{ suggValue(it).main }}</template>
                <i v-else class="bi bi-people" aria-hidden="true"></i>
              </div>
              <div class="value-unit">{{ suggValue(it).unit }}</div>
            </div>

            <div v-if="canEdit && !it.s.confirming" class="sugg-buttons">
              <button type="button" class="btn-primary-sm" @click="startImplement(it.s)">
                <i class="bi bi-check2-circle"></i> {{ $t('actions.btn.implemented') }}
              </button>
              <button type="button" class="btn-ghost-sm" :class="{ on: it.s.pinned }" @click="markConsidering(it.s)">
                <i class="bi bi-hourglass-split"></i> {{ $t('actions.btn.considering') }}
              </button>
              <button type="button" class="btn-ghost-sm" @click="it.s.confirming = 'snooze'">
                <i class="bi bi-clock-history"></i> {{ $t('actions.btn.later') }}
              </button>
              <button type="button" class="btn-ghost-sm danger" @click="dismissLocal(it.s)">
                <i class="bi bi-x-circle"></i> {{ $t('actions.btn.never') }}
              </button>
            </div>

            <!-- Inline confirm: date of the change, or snooze length -->
            <div v-if="it.s.confirming === 'implement'" class="inline-form">
              <label>{{ $t('actions.whenChange') }}
                <input type="date" v-model="it.s.pivotDraft" :max="todayIso" />
              </label>
              <button type="button" class="btn-primary-sm" :disabled="busy" @click="implement(it.s)">{{ $t('actions.btn.trackIt') }}</button>
              <button type="button" class="link-btn" @click="it.s.confirming = false">{{ $t('actions.cancel') }}</button>
            </div>
            <div v-else-if="it.s.confirming === 'snooze'" class="inline-form">
              <label>{{ $t('actions.remindIn') }}
                <select v-model.number="it.s.snoozeDraft">
                  <option :value="14">{{ $t('actions.snoozeFor.w2') }}</option>
                  <option :value="30">{{ $t('actions.snoozeFor.m1') }}</option>
                  <option :value="90">{{ $t('actions.snoozeFor.m3') }}</option>
                </select>
              </label>
              <button type="button" class="btn-primary-sm" @click="snooze(it.s)">{{ $t('actions.btn.snooze') }}</button>
              <button type="button" class="link-btn" @click="it.s.confirming = false">{{ $t('actions.cancel') }}</button>
            </div>
          </li>
        </ul>

        <!-- Held back by what you concluded — quiet, collapsed, at the bottom. -->
        <details v-if="heldBackSuggestions.length && (openFilter === 'all' || openFilter === 'money')" class="held">
          <summary class="held-toggle">
            <i class="bi bi-slash-circle"></i>
            {{ $t('actions.held.count', heldBackSuggestions.length) }}
            <span class="held-cta">
              <span class="held-show">{{ $t('actions.held.show') }}</span><span class="held-hide">{{ $t('actions.held.hide') }}</span>
              <i class="bi bi-chevron-down"></i>
            </span>
          </summary>
          <div v-for="s in heldBackSuggestions" :key="s.idea" class="held-row">
            <div>
              <span class="meta-zone">{{ s.zone }}</span> — {{ s.action }}
              <div class="held-reason">{{ $t('actions.held.reason') }}</div>
            </div>
          </div>
        </details>
      </section>

      <!-- ===================== IN PROGRESS ===================== -->
      <section v-show="stage === 'progress'" class="stage" role="tabpanel" :aria-label="$t('actions.stage.progress.label')">
        <!-- Tracked value: measured is the headline; modelled kept apart -->
        <div v-if="rollup.hasNumbers" class="summary">
          <div class="summary-main">
            <span class="summary-label">{{ $t('actions.rollup.label') }}</span>
            <span class="summary-value">{{ eurSigned(rollup.measured) }}<span class="summary-per"> {{ $t('actions.perMonthSlash') }}</span></span>
            <Tooltip
              :text="$t('actions.rollup.tip')"
              placement="bottom"
              align="start"
            >
              <span class="summary-tag">{{ $t('actions.rollup.measured') }} <i class="bi bi-info-circle"></i></span>
            </Tooltip>
          </div>
          <div class="summary-extra">
            <span v-if="rollup.modelled">{{ $t('actions.rollup.modelled', { eur: eurSigned(rollup.modelled) }) }}</span>
            <span v-if="rollup.building">{{ $t('actions.rollup.building', { n: rollup.building }) }}</span>
          </div>
        </div>

        <div class="toolbar">
          <div class="filters" role="group" :aria-label="$t('actions.aria.filterStatus')">
            <button
              v-for="f in progressFilters"
              :key="f.key"
              type="button"
              class="filter"
              :class="{ active: progressFilter === f.key }"
              :disabled="f.key !== 'all' && !f.count"
              :aria-pressed="progressFilter === f.key"
              @click="progressFilter = f.key"
            >
              {{ f.label }} <span class="filter-n">{{ f.count }}</span>
            </button>
          </div>
        </div>

        <p v-if="!activeActions.length" class="empty-state">
          {{ $t('actions.empty.progress') }}
        </p>

        <ul class="track-list">
          <li
            v-for="a in visibleTracked"
            :key="a.GuidanceActionId"
            class="track"
            :class="['kind-' + kindOf(a), { open: expanded[a.GuidanceActionId] }]"
          >
            <button
              type="button"
              class="track-row"
              :aria-expanded="!!expanded[a.GuidanceActionId]"
              @click="toggleExpanded(a.GuidanceActionId)"
            >
              <span class="track-id">
                <span class="meta">
                  <span class="dot" aria-hidden="true"></span>
                  <span class="meta-kind">{{ kindLabel(kindOf(a)) }}</span>
                  <span class="meta-sep" aria-hidden="true">·</span>
                  <span class="meta-zone">{{ scopeLabel(a) }}</span>
                  <span v-if="noSalesAdded(a)" class="meta-tag tag-nomoney">
                    <i class="bi bi-receipt"></i> {{ $t('actions.tag.noSales') }}
                  </span>
                </span>
                <span class="track-what">{{ a.Data.what }}</span>
                <span v-if="typeChangeLine(a)" class="track-type-note">
                  <i class="bi bi-arrow-repeat"></i> {{ typeChangeLine(a) }}
                </span>
              </span>

              <span v-if="headlineReading(a)" class="track-reading">
                <span class="track-reading-label">{{ headlineReading(a).label }}</span>
                <span class="track-reading-value" :class="headlineReading(a).cls">{{ headlineReading(a).text }}</span>
              </span>

              <span class="track-status">
                <template v-if="a._m.enoughData">
                  <span class="verdict-chip" :class="verdictPill(a._m).cls">{{ verdictPill(a._m).label }}</span>
                  <span class="track-status-note">{{ $t('actions.readyToConclude') }}</span>
                </template>
                <template v-else>
                  <span class="progress" :aria-label="$t('actions.dayOfAria', { d: a._m.elapsedDays || 0 })">
                    <span class="progress-fill" :style="{ width: measureProgress(a) + '%' }"></span>
                  </span>
                  <span class="track-status-note">{{ $t('actions.dayOf', { d: a._m.elapsedDays || 0 }) }}</span>
                </template>
              </span>

              <i class="bi track-chev" :class="expanded[a.GuidanceActionId] ? 'bi-chevron-up' : 'bi-chevron-down'" aria-hidden="true"></i>
            </button>

            <div v-if="expanded[a.GuidanceActionId]" class="track-detail">
              <p class="read" v-html="readSentence(a._m)"></p>
              <p v-if="a._m.weatherCaveat" class="read read-weather">
                <i class="bi bi-cloud-sun"></i> {{ a._m.weatherCaveat }}
              </p>

              <div class="impact" :class="impactView(a._m.impact).cls">
                <i class="bi bi-cash-coin"></i>
                <span v-if="impactView(a._m.impact).kind === 'na'" class="imp-text">{{ $t('actions.impact.na') }}</span>
                <span v-else-if="impactView(a._m.impact).kind === 'none'" class="imp-text">{{ $t('actions.impact.none') }}</span>
                <template v-else>
                  <span class="imp-text">
                    <template v-if="impactView(a._m.impact).early">{{ $t('actions.impact.onTrack') }} </template>
                    <template v-else>{{ $t('actions.impact.label') }} </template>
                    <b>{{ impactView(a._m.impact).value }}</b> {{ $t('actions.impact.perMo') }}
                  </span>
                  <Tooltip :text="impactView(a._m.impact).tip" placement="top" align="start">
                    <span class="imp-basis">{{ impactView(a._m.impact).sub }} <i class="bi bi-info-circle"></i></span>
                  </Tooltip>
                </template>
              </div>

              <div class="delta-row">
                <template v-for="r in a._m.readings" :key="(r.scope || '') + r.metric">
                  <Tooltip v-if="r.deltaPct == null && needsSales(r.metric)" :text="$t('actions.salesTip')" placement="top" align="start">
                    <button type="button" class="delta-chip chip-pending" @click="canEdit && (a._sales = !a._sales)">
                      {{ metricLabel(r.metric, r.scope) }} <span class="pending-tag"><i class="bi bi-plus-lg"></i> {{ $t('actions.addSalesTag') }}</span>
                    </button>
                  </Tooltip>
                  <Tooltip v-else-if="chartable(r.metric)" :text="$t('actions.chartTip')" placement="top" align="start">
                    <button
                      type="button"
                      class="delta-chip"
                      :class="[deltaClass(r), { 'chip-active': a._focus === r.metric }]"
                      @click="a._focus = r.metric"
                    >
                      {{ metricLabel(r.metric, r.scope) }} <strong>{{ levelText(r) }}</strong>
                    </button>
                  </Tooltip>
                  <span v-else class="delta-chip" :class="deltaClass(r)">
                    {{ metricLabel(r.metric, r.scope) }} <strong>{{ levelText(r) }}</strong>
                  </span>
                </template>
              </div>

              <!-- Per-action sales entry (POS surrogate) -->
              <div v-if="a._sales" class="sales-inline">
                <div class="add-head"><strong>{{ $t('actions.sales.title', { name: salesCategoryFor(a) === '__store__' ? $t('actions.sales.wholeStore') : salesCategoryFor(a) }) }}</strong>
                  <span class="add-note">{{ $t('actions.sales.note') }}</span>
                </div>
                <div class="fld-row">
                  <label class="fld"><span>{{ $t('actions.sales.date') }}</span><input type="date" v-model="a._saleDraft.date" :max="todayIso" /></label>
                  <label class="fld"><span>{{ $t('actions.sales.transactions') }}</span><input type="number" min="0" v-model="a._saleDraft.transactions" :placeholder="$t('actions.sales.transactionsPh')" /></label>
                  <label class="fld"><span>{{ $t('actions.sales.revenue') }}</span><input type="number" min="0" v-model="a._saleDraft.revenue" /></label>
                </div>
                <div class="mini-actions">
                  <button class="btn-primary-sm" :disabled="!(Number(a._saleDraft.transactions) > 0) || busy" @click="saveSale(a)">{{ $t('actions.btn.save') }}</button>
                  <button class="link-btn" @click="a._sales = false">{{ $t('actions.btn.close') }}</button>
                </div>
              </div>

              <v-chart v-if="focusReading(a)" class="chart" :option="chartOption(a)" autoresize />
              <p v-if="focusReading(a)" class="chart-caption">
                {{ $t('actions.chartCaption', { metric: metricLabel(a._focus, focusReading(a).scope) }) }}
              </p>

              <div v-if="canEdit && !a._concluding" class="detail-actions">
                <button v-if="noSalesAdded(a) && !a._sales" type="button" class="btn-ghost-sm" @click="a._sales = true">
                  <i class="bi bi-receipt"></i> {{ $t('actions.btn.addSales') }}
                </button>
                <button type="button" class="btn-ghost-sm" @click="startConclude(a)">
                  <i class="bi bi-flag"></i> {{ $t('actions.btn.conclude') }}
                </button>
              </div>
              <div v-else-if="a._concluding" class="conclude-panel">
                <div class="add-head"><strong>{{ $t('actions.conclude.title', { name: scopeLabel(a) }) }}</strong>
                  <span class="add-note">{{ $t('actions.conclude.result') }} <b>{{ verdictPill(a._m).label }}</b>{{ $t('actions.conclude.note') }}</span>
                </div>
                <div class="attr-list">
                  <label v-for="opt in ATTRIBUTIONS" :key="opt.key" class="attr-opt" :class="{ 'attr-sel': a._attr === opt.key }">
                    <input type="radio" :value="opt.key" v-model="a._attr" /> <span>{{ attrText(opt.key, 'label') }}</span>
                  </label>
                </div>
                <label class="fld"><span>{{ $t('actions.conclude.noteLabel') }}</span>
                  <textarea v-model="a._note" rows="2" :placeholder="$t('actions.conclude.notePh')"></textarea>
                </label>
                <div class="mini-actions">
                  <button class="btn-primary-sm" :disabled="busy" @click="confirmConclude(a)">{{ $t('actions.btn.recordConclude') }}</button>
                  <button class="link-btn" @click="a._concluding = false">{{ $t('actions.cancel') }}</button>
                </div>
              </div>
            </div>
          </li>
        </ul>
      </section>

      <!-- ====================== CONCLUDED ====================== -->
      <section v-show="stage === 'done'" class="stage" role="tabpanel" :aria-label="$t('actions.stage.done.label')">
        <p class="stage-intro">
          {{ $t('actions.doneIntro') }}
        </p>
        <p v-if="!concludedActions.length" class="empty-state">{{ $t('actions.empty.done') }}</p>
        <ul class="learned-list">
          <li v-for="c in concludedActions" :key="c.GuidanceActionId" class="learned" :class="'kind-' + kindOf(c)">
            <div class="learned-top">
              <span class="meta">
                <span class="dot" aria-hidden="true"></span>
                <span class="meta-kind">{{ kindLabel(kindOf(c)) }}</span>
                <span class="meta-sep" aria-hidden="true">·</span>
                <span class="meta-zone">{{ scopeLabel(c) }}</span>
                <span class="meta-date">{{ fmtDate(c.ActionDate) }}</span>
              </span>
              <span v-if="outcomeOf(c) || (c._m && c._m.enoughData)" class="learned-chips">
                <span v-if="c._m && c._m.enoughData" class="verdict-chip" :class="verdictPill(c._m).cls">{{ verdictPill(c._m).label }}</span>
                <span v-if="outcomeOf(c)" class="attr-chip" :class="attrMeta(outcomeOf(c).attribution).chip">{{ attrText(attrMeta(outcomeOf(c).attribution).key, 'short') }}</span>
              </span>
            </div>
            <p class="learned-what">{{ c.Data.what }}</p>
            <!-- §9 item 5: a concluded action keeps the result it had that day. -->
            <p v-if="c._m && c._m.measuredUntil" class="learned-measured">
              <i class="bi bi-lock"></i> {{ $t('actions.measuredUntil', { date: dayShort(c._m.measuredUntil) }) }}
            </p>
            <p v-if="outcomeOf(c) && outcomeOf(c).note" class="learned-note">“{{ outcomeOf(c).note }}”</p>
            <p v-if="outcomeOf(c)" class="allen-line"><i class="bi bi-robot"></i> {{ attrText(attrMeta(outcomeOf(c).attribution).key, 'allen') }}</p>
          </li>
        </ul>
      </section>
    </template>
  </div>
</template>

<script>
import { ref, reactive, computed, defineComponent, onMounted, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatNumber, formatEuro, formatPercent, formatDate, formatDuration } from '@/i18n/format'
import { sayDay } from '@/zones/zoneMarkers'
import { useCompanyStore } from '@/stores/CompanyStore'
import Section from '@/components/ui/Section.vue'
import Tooltip from '@/components/ui/Tooltip.vue'
import LogChangeForm from '@/components/guidance/LogChangeForm.vue'
import { useDetectionZoneStore } from '@/stores/DetectionZone'
import { zoneByName, isEntranceLike } from '@/guidance/watchable'
import { serviceSuggestionsFrom } from '@/guidance/serviceSuggestions'
import { useChangesTodayStore } from '@/stores/ChangesToday'
import { NOTICES, ZONE_TYPE_NAMES, typeChangeOf, settingChanged } from '@/zones/changesToday'
import ChangeNotice from '@/components/ui/ChangeNotice.vue'
import { outlookHeadsUp } from '@/weather/weatherPresentation'

import VChart from 'vue-echarts'
import { use } from 'echarts/core'
import { LineChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, MarkLineComponent, MarkAreaComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'

use([LineChart, GridComponent, TooltipComponent, MarkLineComponent, MarkAreaComponent, CanvasRenderer])

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const pad2 = (n) => String(n).padStart(2, '0')
const isoOf = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`

// The funnel: store traffic → zone traffic (÷ store traffic = reach) → clear lingerers
// (÷ zone traffic = engagement) → buyers (÷ clear lingerers = conversion).
// `traffic` is the one metric whose meaning depends on scope — store traffic for a
// whole-store action, zone traffic for a zone — so metricLabel() names it by scope.
const METRIC = {
  traffic: { label: 'Traffic', unit: '' },
  reach: { label: 'Reach', unit: '%' },
  dwell: { label: 'Dwell time', unit: 's' },
  wait: { label: 'Wait time', unit: 's' },
  interaction: { label: 'Interaction time', unit: 's' },
  engagement: { label: 'Engagement', unit: '%' },
  conversion: { label: 'Conversion', unit: '%' },
  purchase_rate: { label: 'Purchase rate', unit: '%' },
  // Customers nobody helped, per open day — fewer is the win.
  service_response: { label: 'Not helped', unit: '' },
  // Entrance actions: the whole store's entries (the door's own count is 'traffic').
  store_traffic: { label: 'Store traffic', unit: '' },
  // Tripwire estimates, pooled per before/after window (no daily chart).
  dwell_est: { label: 'Time in zone (est.)', unit: 's' },
  transit: { label: 'Transit time (est.)', unit: 's' },
}
const CHARTABLE = ['traffic', 'reach', 'dwell', 'wait', 'interaction', 'engagement', 'conversion', 'purchase_rate', 'service_response']
const SALES_METRICS = ['conversion', 'purchase_rate']

const ATTRIBUTIONS = [
  { key: 'worked', label: 'It worked — keep it', short: 'Worked', chip: 'attr-good',
    allen: 'Allen will look to repeat this where it fits, and won’t re-pitch it here.' },
  { key: 'exec', label: 'Idea’s sound — execution fell short', short: 'Execution fell short', chip: 'attr-warn',
    allen: 'Allen keeps the idea alive — it may resurface with guidance to execute it properly.' },
  { key: 'drop', label: 'Didn’t fit here — don’t suggest again', short: 'Won’t suggest again', chip: 'attr-bad',
    allen: 'Allen won’t suggest this for this zone again.' },
  { key: 'inconclusive', label: 'Inconclusive — set aside', short: 'Inconclusive', chip: 'attr-flat',
    allen: 'Set aside — not counted for or against the idea.' },
]
const ATTR_BY_KEY = Object.fromEntries(ATTRIBUTIONS.map((a) => [a.key, a]))

// Lever → metrics to watch (product-zone opportunities). Each lever is judged on its
// OWN funnel step, with the step above it shown for context.
const LEVER_METRICS = {
  // Reach is judged on reach, never on engagement: pulling more (and more casual)
  // visitors into a zone usually DILUTES the stop rate, so a reach change that worked
  // would have read as "Declined". Zone traffic sits beside it to show why reach moved.
  reach: ['reach', 'traffic'],
  eng: ['engagement', 'traffic'],
  conv: ['conversion', 'engagement', 'traffic'],
  // Service suggestions watch ONLY service_response. Adding traffic here would be
  // noise: a rota change is not expected to move footfall, and a metric that moves
  // for unrelated reasons muddies the verdict on the one that should.
  svc: ['service_response'],
  // A checkout staffing change is judged on the WAIT, which is already a registered
  // lever — so opening a second till gets a real before/after with no new metric.
  chk: ['wait'],
}

const LEVER_LABEL = { reach: 'Reach', eng: 'Engagement', conv: 'Conversion' }
// Lever labels are English data on a suggestion (here and in serviceSuggestions.js);
// shown through actions.lever.*.
const LEVER_KEY = { Reach: 'reach', Engagement: 'engagement', Conversion: 'conversion', Service: 'service', Checkout: 'checkout' }

// The KIND of an action — which group of suggestions it came from — derived from its
// existing `idea` key, so no schema change and older actions classify themselves:
// svc:/chk: → customers left waiting, reach-visitors: → more shoppers to a (tripwire)
// zone, any other suggestion → worth money, no idea → your own idea.
const KIND_LABEL = {
  money: 'Worth money', waiting: 'Customers left waiting',
  reach: 'More shoppers to a zone', own: 'Your own idea',
}
const KIND_ORDER = ['money', 'waiting', 'reach', 'own']
const kindOf = (a) => {
  const idea = (a && a.Data && a.Data.idea) || ''
  if (!idea) return 'own'
  if (/^(svc|chk):/.test(idea)) return 'waiting'
  if (/^reach-visitors:/.test(idea)) return 'reach'
  return 'money'
}
const byKind = (arr) => [...arr].sort((x, y) => KIND_ORDER.indexOf(kindOf(x)) - KIND_ORDER.indexOf(kindOf(y)))
const slugOf = (zone) => String(zone).toLowerCase().replace(/\s+/g, '-')

const ideaSlug = (zone, leverKind) => `${leverKind}:${String(zone).toLowerCase().replace(/\s+/g, '-')}`

export default defineComponent({
  name: 'GuidanceTab',
  components: { Section, Tooltip, VChart, LogChangeForm, ChangeNotice },
  props: {
    opportunities: { type: Array, default: () => [] },   // computePriorities output
    canEdit: { type: Boolean, default: true },
    isActive: { type: Boolean, default: true },
    focusZone: { type: String, default: '' },   // deep-link: scroll to + flash this zone's card
    weatherOutlook: { type: Object, default: null },   // weather_signature.outlook (#2)
  },

  setup(props) {
    const store = useCompanyStore()
    // English keeps the exact output it always had; Dutch words, numbers, € and
    // dates go through vue-i18n and i18n/format.js. Labels, metric keys, verdict keys
    // and attribution keys stay English DATA — only what's shown is translated.
    const { t, locale } = useI18n()
    const isNl = () => locale.value === 'nl'
    const loading = ref(true)

    // #2 timely weather heads-up (forward outlook → prep the weather-led zone).
    const WX_DISMISS_KEY = 'wxHeadsUpDismissedId'
    const weatherHeadsUp = computed(() => outlookHeadsUp(props.weatherOutlook))
    const wxDismissedId = ref((typeof localStorage !== 'undefined' && localStorage.getItem(WX_DISMISS_KEY)) || '')
    const showWeatherHeadsUp = computed(() => {
      const h = weatherHeadsUp.value
      if (!h) return false
      if (wxDismissedId.value && wxDismissedId.value === h.id) return false   // dismissed this episode
      if (h.validThrough && new Date() > new Date(`${h.validThrough}T23:59:59`)) return false   // auto-expire
      return true
    })
    const dismissWeatherHeadsUp = () => {
      const h = weatherHeadsUp.value
      if (!h) return
      wxDismissedId.value = h.id
      try { localStorage.setItem(WX_DISMISS_KEY, h.id) } catch (_) { /* private mode */ }
    }
    const busy = ref(false)
    const actions = ref([])            // guidance actions with ._m (measure) attached
    const recommendations = ref({})    // {zone: {action, why, source}} — Allen's per-zone copy
    const todayIso = isoOf(new Date())
    const suggestionStates = ref({})   // {idea: {state, snoozeUntil}} — server-backed (durable)

    // ---------------- load ----------------
    const load = async () => {
      loading.value = true
      try {
        const [recs, states, finds] = await Promise.all([
          store.fetchGuidanceRecommendations().catch(() => ({})),
          store.fetchGuidanceSuggestionStates().catch(() => ({})),
          store.fetchFindings().catch(() => []),
        ])
        recommendations.value = recs
        suggestionStates.value = states
        buildServiceSuggestions(finds)
        buildSuggestions()   // rebuild now that Allen's per-zone copy is in
        if (!(zoneStore.detectionzones || []).length) {
          await zoneStore.fetchDetectionZones().catch(() => {})
        }
        const rows = await store.fetchGuidanceActions()
        // Measure every action — active ones to render, concluded 'worked' ones so
        // their earnings still count toward the lifetime total.
        const measures = await Promise.all(rows.map((r) => store.measureGuidanceAction(r.GuidanceActionId).catch(() => null)))
        rows.forEach((r, i) => {
          r._m = measures[i] || { readings: [], impact: { confidence: 'na' }, elapsedDays: 0, enoughData: false, pivot: r.ActionDate }
          const goal = ((r.Data && r.Data.metrics) || [])[0]
          const first = (r._m.readings || []).find((x) => x.metric === goal && CHARTABLE.includes(x.metric) && x.deltaPct != null)
            || (r._m.readings || []).find((x) => CHARTABLE.includes(x.metric) && x.deltaPct != null)
            || (r._m.readings || []).find((x) => CHARTABLE.includes(x.metric))
          r._focus = first ? first.metric : null
          r._sales = false
          r._saleDraft = { date: todayIso, transactions: null, revenue: null }
          r._concluding = false
          r._attr = null
          r._note = ''
        })
        actions.value = rows
      } catch (e) {
        actions.value = []
      } finally {
        loading.value = false
      }
    }
    onMounted(() => { if (props.isActive) load() })
    watch(() => props.isActive, (v) => { if (v && loading.value) load() })

    const activeActions = computed(() => actions.value.filter((a) => a.Status === 'active'))
    const concludedActions = computed(() => actions.value.filter((a) => a.Status === 'concluded'))

    // ---------------- suggestions ----------------
    // An action's suggestion idea in every form the server knows (zone name or key,
    // any earlier name — `ideaForms`, phase 4.4), so it matches whichever form a
    // suggestion is built in.
    const ideaFormsOf = (a) => {
      const d = (a && a.Data) || {}
      return (Array.isArray(d.ideaForms) && d.ideaForms.length) ? d.ideaForms : (d.idea ? [d.idea] : [])
    }
    const concludedByIdea = computed(() => {
      const m = {}
      for (const a of concludedActions.value) {
        const out = outcomeOf(a)
        if (out) for (const idea of ideaFormsOf(a)) m[idea] = out.attribution
      }
      return m
    })
    // "why" composed from the zone's real pattern + lever (approximates the demo's
    // bespoke copy from live data; the LLM recommender can replace it later).
    const composeWhy = (o) => {
      const bits = []
      // Skip the uninformative 'Mid-tier' (neutral) label — same reason it's dropped
      // from the Prio cards.
      if (o.patternLabel && o.patternKind !== 'neutral') bits.push(o.patternLabel)
      if (o.leverExplainer) bits.push(`the bigger lever here is ${o.leverExplainer}`)
      return bits.join(' — ') || null
    }
    // Service suggestions come from the proactive findings feed (guidance/serviceSuggestions).
    const serviceSuggestions = ref([])
    const buildServiceSuggestions = (rows) => {
      serviceSuggestions.value = serviceSuggestionsFrom(rows, todayIso)
    }

    const suggestions = ref([])
    const buildSuggestions = () => {
      // A suggestion needs Allen's recommendation (from the nightly run). No rec →
      // no suggestion; Prio shows "recommendation appears after the next update".
      suggestions.value = (props.opportunities || [])
        .filter((o) => o && o.zone && (o.potentialImpact || 0) > 0 && o.kind !== 'strong'
                       && recommendations.value[o.zone])
        .map((o) => {
          const leverKind = o.leverKind || (o.bestLever === 'Increase Conversion' ? 'conv' : 'eng')
          const rec = recommendations.value[o.zone]
          return {
            idea: ideaSlug(o.zone, leverKind), zone: o.zone, leverKind,
            leverLabel: LEVER_LABEL[leverKind] || 'Engagement',
            eur: Math.round(o.potentialImpact || 0),
            action: rec.action,
            why: rec.why || null,
            state: 'considering', confirming: false, pivotDraft: todayIso, snoozeDraft: 14,
          }
        })
    }
    watch(() => props.opportunities, buildSuggestions, { immediate: true })
    watch(recommendations, buildSuggestions)   // rebuild when the nightly recs load

    // Reach suggestions for TRIPWIRE zones: sized in visitors, never €. A tripwire
    // counts who arrives, not who stops or buys, so the ladder expresses its reach gap
    // as extra zone visitors (withinReachUnit 'visitors', potentialImpact 0) — which
    // the €-list above filters out. The basis (current reach vs its own best weeks and
    // its normal swing) is shown on the card as the reason it fires.
    const reachSuggestions = ref([])
    const pct1 = (x) => (isNl()
      ? formatPercent(Number(x) * 100, { decimals: 1, lang: 'nl' })
      : `${(Number(x) * 100).toFixed(1)}%`)
    const buildReachSuggestions = () => {
      reachSuggestions.value = (props.opportunities || [])
        .filter((o) => o && o.zone && o.withinReachUnit === 'visitors'
                       && (o.withinReachMagnitude || 0) > 0 && recommendations.value[o.zone])
        .map((o) => {
          const rec = recommendations.value[o.zone]
          const leg = (o.ladder && o.ladder.legs && o.ladder.legs.reach) || {}
          // Raw rates; basisText() formats them in the reader's language.
          const basis = leg.activeName === 'your best weeks' && leg.active != null
            ? { now: o.reachRate, best: leg.active,
                wobble: o.reachWobble != null ? o.reachWobble : null }
            : null
          return {
            idea: `reach-visitors:${slugOf(o.zone)}`, zone: o.zone, leverKind: 'reach',
            leverLabel: 'Reach', visitors: Math.round(o.withinReachMagnitude),
            action: rec.action, why: rec.why || null, basis,
            state: 'considering', confirming: false, pivotDraft: todayIso, snoozeDraft: 14,
          }
        })
    }
    watch(() => props.opportunities, buildReachSuggestions, { immediate: true })
    watch(recommendations, buildReachSuggestions)

    // Ideas already being tracked (an active action exists) — hide their suggestion
    // so it can't be added twice. Delete the action and it returns to the pool.
    const trackedIdeas = computed(() => new Set(
      actions.value.filter((a) => a.Status === 'active').flatMap(ideaFormsOf)))

    const isHeldBack = (s) => concludedByIdea.value[s.idea] === 'drop'
    const execFlagFor = (s) => (concludedByIdea.value[s.idea] === 'exec'
      ? t('actions.execFlag') : null)
    const isSuppressed = (s) => {
      const st = suggestionStates.value[s.idea]
      if (!st) return false
      if (st.state === 'dismissed') return true
      if (st.state === 'snoozed' && st.snoozeUntil && st.snoozeUntil > todayIso) return true
      return false
    }
    const activeSuggestions = computed(() =>
      suggestions.value.filter((s) => !isSuppressed(s) && !isHeldBack(s) && !trackedIdeas.value.has(s.idea)))
    // Identical rules — dismissing, snoozing and "already tracked" behave the same
    // whether a suggestion carries a € figure or not.
    const activeServiceSuggestions = computed(() =>
      serviceSuggestions.value.filter((s) => !isSuppressed(s) && !isHeldBack(s)
                                          && !trackedIdeas.value.has(s.idea)))
    const activeReachSuggestions = computed(() =>
      reachSuggestions.value.filter((s) => !isSuppressed(s) && !isHeldBack(s)
                                        && !trackedIdeas.value.has(s.idea)))
    const heldBackSuggestions = computed(() =>
      suggestions.value.filter((s) => isHeldBack(s) && !trackedIdeas.value.has(s.idea)))

    // Deep-link from Today's "Act on this →": scroll to the incoming zone's card and
    // flash it once, so the same opportunity object continues visually into Actions.
    const rootEl = ref(null)
    const flashZone = ref('')

    // ---------------- page layout: one lifecycle stage at a time ----------------
    // `?stage=progress|done` deep-links straight to a stage.
    const _qStage = typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('stage') : null
    const stage = ref(['open', 'progress', 'done'].includes(_qStage) ? _qStage : 'open')
    const openFilter = ref('all')
    const progressFilter = ref('all')
    const expanded = reactive({})
    const toggleExpanded = (id) => { expanded[id] = !expanded[id] }
    let _flashedFor = null
    const focusOnZone = () => {
      const z = props.focusZone
      if (!z || !props.isActive || _flashedFor === z) return
      stage.value = 'open'
      openFilter.value = 'all'
      nextTick(() => {
        const root = rootEl.value
        if (!root) return
        const sel = (window.CSS && CSS.escape) ? CSS.escape(z) : z
        const el = root.querySelector(`[data-zone="${sel}"]`)
        if (!el) return
        _flashedFor = z
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        flashZone.value = z
        setTimeout(() => { if (flashZone.value === z) flashZone.value = '' }, 2200)
      })
    }
    watch([() => props.focusZone, () => props.isActive, activeSuggestions], focusOnZone, { immediate: true })

    // Persist dismiss/snooze/consider server-side (optimistic local update).
    const applyState = async (s, state, snoozeDays = null) => {
      const snoozeUntil = state === 'snoozed'
        ? isoOf(new Date(Date.now() + (snoozeDays || 14) * 86400000)) : null
      suggestionStates.value = { ...suggestionStates.value, [s.idea]: { state, snoozeUntil } }
      s.confirming = false
      try { await store.setGuidanceSuggestionState(s.idea, state, snoozeDays) } catch (e) { /* keep optimistic */ }
    }
    const dismissLocal = (s) => applyState(s, 'dismissed')
    const snooze = (s) => applyState(s, 'snoozed', s.snoozeDraft)
    const consider = (s) => applyState(s, 'active')
    const startImplement = (s) => { s.confirming = 'implement' }
    const implement = async (s) => {
      busy.value = true
      try {
        await store.createGuidanceAction({
          what: s.action,
          // A checkout suggestion is store-scoped; the guidance engine measures `wait`
          // across the checkout waiting zones rather than a zone literally named
          // "Checkout", so it is logged with no zone list.
          zoneNames: s.leverKind === 'chk' ? [] : [s.zone],
          actionDate: s.pivotDraft || todayIso,
          metrics: LEVER_METRICS[s.leverKind] || ['engagement', 'traffic'],
          salesCategory: s.leverKind === 'conv' ? s.zone : null,
          idea: s.idea,
          sourceSuggestionSignature: s.idea,
        })
        s.confirming = false
        await load()
      } finally { busy.value = false }
    }

    // ---------------- add action ----------------
    const showAddForm = ref(false)
    // Every active zone of the store (the form offers each only what it can measure).
    const zoneStore = useDetectionZoneStore()

    // Zone identity 6.3: an action in progress on a zone that changed type today stays
    // (history), with a line saying so. Its suggestions are already gone: the summary
    // and the findings feed leave the zone out until the nightly run.
    const changesToday = useChangesTodayStore()
    changesToday.ensureLoaded(null)
    const typeChangeLine = (a) => {
      if (!a || a.Status !== 'active') return null
      const d = a.Data || {}
      const names = d.zoneNames || []
      const keys = d.zoneKeys || []
      const hit = names.map((n, i) => typeChangeOf(changesToday.register, { key: keys[i] ?? null, name: n }))
        .find(Boolean)
      if (!hit) return null
      const when = hit.at ? new Date(hit.at) : new Date()
      return NOTICES.actionLine(when.toLocaleDateString(isNl() ? 'nl' : 'en-GB', { day: 'numeric', month: 'long' }),
        ZONE_TYPE_NAMES[hit.to] || 'zone of another type')
    }
    const liveZones = computed(() => zoneStore.detectionzones || [])
    const addAction = async ({ what, zone, pivot, metrics }) => {
      busy.value = true
      try {
        const z = zoneByName(liveZones.value, zone)
        // Sales category: conversion / purchase rate read sales for the zone itself,
        // the store-wide bucket for the whole store.
        const needsCat = metrics.includes('conversion') || metrics.includes('purchase_rate')
        await store.createGuidanceAction({
          what,
          zoneNames: z ? [z.DetectionZoneName] : [],
          actionDate: pivot || todayIso,
          metrics,          // the goal first — it judges the action
          salesCategory: needsCat ? (z ? z.DetectionZoneName : '__store__') : null,
        })
        showAddForm.value = false
        await load()
        stage.value = 'progress'   // show the new action where it now lives
      } finally { busy.value = false }
    }

    // ---------------- sales ----------------
    // The sales category an action's sales go under: its own, else the zone, else the
    // store-wide '__store__' bucket (what purchase rate reads store-wide).
    const salesCategoryFor = (a) => a.Data.salesCategory
      || ((a.Data.zoneNames && a.Data.zoneNames[0]) || '__store__')
    // Tripwire-only = a line but no shape, read from the LIVE zone's own geometry. The
    // opportunity list is keyed by the name in the nightly figures, which still carry
    // the old one on the day of a rename — a wrong answer here is saved for good.
    const _hasGeom = (raw, min) => {
      try {
        const v = typeof raw === 'string' ? JSON.parse(raw || 'null') : raw
        return Array.isArray(v) && v.length >= min
      } catch { return false }
    }
    const isTripwireZone = (zone) => {
      const z = zoneByName(liveZones.value, zone)
      if (z) return _hasGeom(z.DetectionZoneTripwirePoints, 1) && !_hasGeom(z.DetectionZonePointArray, 3)
      const o = (props.opportunities || []).find((x) => x.zone === zone)
      return !!(o && o.capability && o.capability.countsOnly)
    }
    const saveSale = async (a) => {
      const tx = Number(a._saleDraft.transactions)
      if (!(tx > 0)) return
      busy.value = true
      try {
        // First sales on an action that isn't watching a sales metric (e.g. a reach
        // change, or your own idea): link the category and start measuring the zone's
        // conversion — or purchase rate for a tripwire zone / the whole store, which
        // have no engaged visitors to divide by. Without this the sales were saved but
        // never read, because measurement only looks at the action's own category.
        const metrics = (a.Data.metrics || []).slice()
        if (!metrics.some((m) => SALES_METRICS.includes(m))) {
          const zone = a.Data.zoneNames && a.Data.zoneNames[0]
          metrics.push(zone && !isTripwireZone(zone) ? 'conversion' : 'purchase_rate')
          const cat = salesCategoryFor(a)
          await store.updateGuidanceAction(a.GuidanceActionId, { metrics, salesCategory: cat })
          a.Data = { ...a.Data, metrics, salesCategory: cat }
        }
        await store.upsertGuidanceSale({
          saleDate: a._saleDraft.date,
          category: salesCategoryFor(a),
          transactions: tx,
          revenue: a._saleDraft.revenue ? Number(a._saleDraft.revenue) : null,
        })
        a._saleDraft.transactions = null; a._saleDraft.revenue = null
        // re-measure this action so conversion/€ pick up the new sales
        a._m = await store.measureGuidanceAction(a.GuidanceActionId)
      } finally { busy.value = false }
    }

    // ---------------- conclude ----------------
    const startConclude = (a) => {
      a._attr = verdictPill(a._m).key === 'improved' ? 'worked' : 'inconclusive'
      a._concluding = true
    }
    const confirmConclude = async (a) => {
      busy.value = true
      try {
        await store.updateGuidanceAction(a.GuidanceActionId, {
          status: 'concluded',
          outcome: { attribution: a._attr, note: (a._note || '').trim() },
        })
        await load()
      } finally { busy.value = false }
    }

    // ---------------- presentation ----------------
    const fmtInt = (n) => (isNl()
      ? formatNumber(Math.round(n || 0), { lang: 'nl' })
      : Math.round(n || 0).toLocaleString('en-US'))
    const eurAbs = (n) => {
      const v = Math.round(Math.abs(n || 0) / 10) * 10
      return isNl() ? formatEuro(v, { lang: 'nl' }) : `€${v.toLocaleString('en-US')}`
    }
    const eurSigned = (n) => `${(n || 0) < 0 ? '−' : '+'}${eurAbs(n)}`
    const fmtDelta = (p) => {
      if (p == null) return '—'
      if (isNl()) return `${p > 0 ? '+' : ''}${formatPercent(p, { decimals: 1, lang: 'nl' })}`
      return `${p > 0 ? '+' : ''}${Number(p).toFixed(1)}%`
    }
    // `scope` = the zone name, or null for the whole store (a reading's `scope` field).
    // METRIC's English labels are the English on screen; Dutch from actions.metric.*.
    const metricLabel = (m, scope) => {
      if (m === 'traffic') {
        if (!scope) return t('actions.metric.storeTraffic')
        return isEntranceLike(zoneByName(liveZones.value, scope)) ? t('actions.metric.doorTraffic') : t('actions.metric.zoneTraffic')
      }
      return METRIC[m] ? t(`actions.metric.${m}`) : m
    }
    const metricUnit = (m) => (METRIC[m] ? METRIC[m].unit : '')
    const chartable = (m) => CHARTABLE.includes(m)
    const needsSales = (m) => SALES_METRICS.includes(m)
    const scopeLabel = (a) => ((a.Data.zoneNames && a.Data.zoneNames[0]) || t('actions.wholeStore'))
    const outcomeOf = (a) => (a.Data && a.Data.outcome) || null
    const attrMeta = (k) => ATTR_BY_KEY[k] || ATTR_BY_KEY.inconclusive
    // ATTRIBUTIONS' English is the English on screen; field = 'label' | 'short' | 'allen'.
    const attrText = (key, field) => t(`actions.attr.${key}.${field}`)
    const kindLabel = (k) => (KIND_LABEL[k] ? t(`actions.kind.${k}`) : k)
    const leverText = (label) => (LEVER_KEY[label] ? t(`actions.lever.${LEVER_KEY[label]}`) : label)
    // A checkout-staffing suggestion is store-level: its "zone" is the word Checkout.
    const zoneShown = (s) => (s.leverKind === 'chk' && s.zone === 'Checkout' ? t('actions.lever.checkout') : s.zone)
    // "12 Oct" / "12 okt".
    const dayShort = (d) => (isNl() ? formatDate(String(d).slice(0, 10), { style: 'short', lang: 'nl' }) : sayDay(d))

    const fmtDate = (iso) => {
      if (isNl()) return formatDate(String(iso).slice(0, 10), { style: 'medium', lang: 'nl' })
      const d = new Date(String(iso).slice(0, 10) + 'T00:00:00')
      return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`
    }
    // Chart axis day: "12 Oct" / "12 okt".
    const axisDay = (iso) => {
      if (isNl()) return formatDate(String(iso).slice(0, 10), { style: 'short', lang: 'nl' })
      const d = new Date(String(iso).slice(0, 10) + 'T00:00:00')
      return `${d.getDate()} ${MONTHS[d.getMonth()]}`
    }

    const deltaClass = (r) => {
      if (r.deltaPct == null) return 'flat'
      const gd = r.goodDir || 0
      if (!gd) return 'flat'
      const eff = r.deltaPct * gd
      if (eff >= 4) return 'up'
      if (eff <= -4) return 'down'
      return 'flat'
    }

    // `key` is data (startConclude compares it); label + hint are what's shown.
    const pill = (key, cls) => ({ key, cls, label: t(`actions.verdict.${key}.label`), hint: t(`actions.verdict.${key}.hint`) })
    const verdictPill = (m) => {
      if (!m || !m.enoughData) return pill('building_up', 'v-early')
      let up = 0, down = 0
      for (const r of m.readings || []) {
        const gd = r.goodDir || 0
        if (!gd || r.deltaPct == null) continue
        const eff = r.deltaPct * gd
        if (eff >= 4) up++; else if (eff <= -4) down++
      }
      if (up && down) return pill('mixed', 'v-mixed')
      if (up) return pill('improved', 'v-improved')
      if (down) return pill('declined', 'v-declined')
      // Only direction-less metrics (transit time) moved: say so, don't judge it.
      const moved = (m.readings || []).some((r) => !r.goodDir && r.deltaPct != null && Math.abs(r.deltaPct) >= 4)
      if (moved) return pill('changed', 'v-flat')
      return pill('no_change', 'v-flat')
    }

    // A metric's LEVEL in its own unit — rates as %, traffic and unhelped customers per
    // day, times in seconds — so a rate and a count never look like the same number.
    // The % change still decides the colour.
    const fmtLevel = (m, v) => {
      if (v == null) return '—'
      if (isNl()) {
        if (m === 'traffic') return formatNumber(Math.round(v), { lang: 'nl' })
        if (m === 'service_response') return formatNumber(v, { decimals: 1, lang: 'nl' })
        const un = metricUnit(m)
        if (un === 's') return formatDuration(v, { lang: 'nl' })
        if (un === '%') return formatPercent(v, { decimals: 1, lang: 'nl' })
        return formatNumber(v, { decimals: Number.isInteger(Number(v)) ? 0 : 1, lang: 'nl' })
      }
      if (m === 'traffic') return `${Math.round(v)}`
      if (m === 'service_response') return Number(v).toFixed(1)
      const u = metricUnit(m)
      if (u === 's') return `${Math.round(v)}s`
      if (u === '%') return `${Number(v).toFixed(1)}%`
      return `${v}`
    }
    const isPerDay = (r) => r.metric === 'traffic' || r.metric === 'service_response'
    const levelText = (r) => {
      if (r.before == null || r.after == null) return fmtDelta(r.deltaPct)
      const p = { before: fmtLevel(r.metric, r.before), after: fmtLevel(r.metric, r.after) }
      return isPerDay(r) ? t('actions.level.perDay', p) : t('actions.level.plain', p)
    }
    const readSentence = (m) => {
      const parts = (m.readings || []).filter((r) => r.deltaPct != null).map((r) => {
        const label = metricLabel(r.metric, r.scope).toLowerCase()
        if (Math.abs(r.deltaPct) < 4) {
          if (r.before == null) return `<strong>${t('actions.read.flat', { label })}</strong>`
          const p = { label, level: fmtLevel(r.metric, r.before) }
          return `<strong>${isPerDay(r) ? t('actions.read.flatAtPerDay', p) : t('actions.read.flatAt', p)}</strong>`
        }
        return `<strong>${t('actions.read.moved', { label, change: levelText(r) })}</strong>`
      })
      if (!parts.length) return t('actions.read.measuring')
      const joined = parts.length > 1
        ? t('actions.read.list', { list: parts.slice(0, -1).join(', '), last: parts.slice(-1)[0] })
        : parts[0]
      return t('actions.read.since', { date: fmtDate(m.pivot), parts: joined })
    }

    // The € basis, by key (measured / estimated / service): actions.basisShort.* /
    // actions.basisFull.*.
    const BASES = ['measured', 'estimated', 'service']
    const impactView = (imp) => {
      if (!imp || imp.confidence === 'na') return { kind: 'na', cls: 'imp-na' }
      if (imp.confidence === 'none') return { kind: 'none', cls: 'imp-none' }
      const neg = imp.eur < 0
      const cls = neg ? 'imp-bad' : imp.confidence === 'good' ? 'imp-good' : imp.confidence === 'early' ? 'imp-early' : 'imp-indicative'
      const value = imp.confidence === 'early' ? `${neg ? '−' : ''}${eurAbs(imp.low)}–${eurAbs(imp.high)}` : eurSigned(imp.eur)
      return {
        kind: 'value', cls, early: imp.confidence === 'early', value,
        sub: t(`actions.basisShort.${BASES.includes(imp.basis) ? imp.basis : 'estimated'}`),
        tip: (BASES.includes(imp.basis) ? t(`actions.basisFull.${imp.basis}`) : '')
          + (imp.confidence === 'early' ? t('actions.basisEarly') : ''),
      }
    }

    const focusReading = (a) => (a._m.readings || []).find((r) => r.metric === a._focus && (r.daily || []).length)
    const chartOption = (a) => {
      const r = focusReading(a)
      if (!r) return {}
      const labels = r.daily.map((p) => axisDay(p.date))
      const values = r.daily.map((p) => p.value)
      const pivotLabel = axisDay(a._m.pivot)
      const unit = metricUnit(a._focus)
      // Dutch: "12,5%", "48 sec"; English unchanged ("12.5%", "48s").
      const nlValue = (v) => {
        if (v == null || v === '') return '—'
        const num = formatNumber(v, { decimals: Number.isInteger(Number(v)) ? 0 : 1, lang: 'nl' })
        return unit === 's' ? `${num} sec` : `${num}${unit}`
      }
      const st = deltaClass(r)
      const after = st === 'up' ? 'rgba(178,193,73,0.16)' : st === 'down' ? 'rgba(176,70,75,0.12)' : 'rgba(148,163,184,0.12)'
      return {
        animation: false,
        grid: { left: 42, right: 16, top: 18, bottom: 46 },
        tooltip: { trigger: 'axis', valueFormatter: (v) => (isNl() ? nlValue(v) : `${v}${unit}`) },
        xAxis: { type: 'category', data: labels, axisLabel: { fontSize: 10, color: '#7a8a96', interval: Math.ceil(labels.length / 8) }, axisLine: { lineStyle: { color: '#e6edf3' } } },
        yAxis: { type: 'value', axisLabel: { fontSize: 10, color: '#7a8a96', formatter: isNl() ? nlValue : `{value}${unit}` }, splitLine: { lineStyle: { color: '#f1f5f9' } } },
        series: [{
          type: 'line', data: values, smooth: true, symbol: 'none',
          lineStyle: { width: 2, color: '#4F7EB3' }, areaStyle: { color: 'rgba(79,126,179,0.08)' },
          markArea: { silent: true, data: [
            [{ xAxis: labels[0], itemStyle: { color: 'rgba(148,163,184,0.10)' } }, { xAxis: pivotLabel }],
            [{ xAxis: pivotLabel, itemStyle: { color: after } }, { xAxis: labels[labels.length - 1] }],
          ] },
          markLine: { silent: true, symbol: 'none', lineStyle: { color: '#B2C149', type: 'dashed', width: 2 }, label: { formatter: t('actions.chartChange'), color: '#7a8a3f', fontSize: 10 }, data: [{ xAxis: pivotLabel }] },
        }],
      }
    }

    // ---------------- roll-up ----------------
    const rollup = computed(() => {
      let measured = 0, modelled = 0, building = 0
      for (const a of activeActions.value) {
        const imp = a._m.impact || {}
        if (imp.confidence === 'good') measured += imp.eur
        else if (imp.confidence === 'indicative') modelled += imp.eur
        else if (imp.confidence === 'early') building += 1
      }
      return { measured, modelled, building, hasNumbers: measured !== 0 || modelled !== 0 }
    })

    // ---------------- lifecycle stages + filters ----------------
    const openItems = computed(() => [
      ...activeSuggestions.value.map((s) => ({ s, kind: 'money' })),
      ...activeServiceSuggestions.value.map((s) => ({ s, kind: 'waiting' })),
      ...activeReachSuggestions.value.map((s) => ({ s, kind: 'reach' })),
    ])
    const stages = computed(() => [
      { key: 'open', label: t('actions.stage.open.label'), hint: t('actions.stage.open.hint'), count: openItems.value.length },
      { key: 'progress', label: t('actions.stage.progress.label'), hint: t('actions.stage.progress.hint'), count: activeActions.value.length },
      { key: 'done', label: t('actions.stage.done.label'), hint: t('actions.stage.done.hint'), count: concludedActions.value.length },
    ])
    const openFilters = computed(() => [
      { key: 'all', label: t('actions.filter.all'), count: openItems.value.length },
      ...['money', 'waiting', 'reach'].map((k) => ({
        key: k, label: kindLabel(k), count: openItems.value.filter((i) => i.kind === k).length,
      })),
    ])
    const visibleOpen = computed(() => (openFilter.value === 'all'
      ? openItems.value : openItems.value.filter((i) => i.kind === openFilter.value)))
    // The card's headline figure, in its own unit.
    const suggValue = (it) => {
      if (it.kind === 'money') {
        return { main: isNl() ? formatEuro(Math.round(it.s.eur || 0), { lang: 'nl' }) : `€${fmtInt(it.s.eur)}`, unit: t('actions.value.perMonth') }
      }
      if (it.kind === 'reach') return { main: `+${fmtInt(it.s.visitors)}`, unit: t('actions.value.visitorsPerMonth') }
      return { main: null, unit: t('actions.value.staffing') }
    }
    const basisText = (b) => {
      const p = { now: pct1(b.now), best: pct1(b.best) }
      // wobble: the normal week-to-week swing, in points to one decimal.
      if (b.wobble == null) return t('actions.basis.plain', p)
      const w = isNl() ? formatNumber(b.wobble * 100, { decimals: 1, lang: 'nl' }) : (b.wobble * 100).toFixed(1)
      return t('actions.basis.wobble', { ...p, wobble: w })
    }
    const markConsidering = (s) => { consider(s); s.pinned = true }

    const isReady = (a) => !!(a._m && a._m.enoughData)
    const progressFilters = computed(() => [
      { key: 'all', label: t('actions.filter.all'), count: activeActions.value.length },
      { key: 'ready', label: t('actions.filter.ready'), count: activeActions.value.filter(isReady).length },
      { key: 'measuring', label: t('actions.filter.measuring'), count: activeActions.value.filter((a) => !isReady(a)).length },
    ])
    const visibleTracked = computed(() => {
      const ready = byKind(activeActions.value.filter(isReady))
      const measuring = byKind(activeActions.value.filter((a) => !isReady(a)))
      if (progressFilter.value === 'ready') return ready
      if (progressFilter.value === 'measuring') return measuring
      return [...ready, ...measuring]
    })
    // The row's headline is the action's GOAL metric — the first metric it was logged
    // with, i.e. the suggestion's lever (or your first pick for your own idea). Never a
    // fallback: a goal that can't be read yet says so instead of showing another metric.
    const headlineReading = (a) => {
      const goal = (a.Data.metrics || [])[0]
      const r = (a._m.readings || []).find((x) => x.metric === goal)
      if (!r) return null
      const label = t('actions.goal', { metric: metricLabel(r.metric, r.scope) })
      if (r.deltaPct == null && needsSales(r.metric)) return { label, text: t('actions.needsSales'), cls: 'rd-pending' }
      return { label, text: levelText(r), cls: 'rd-' + deltaClass(r) }
    }
    const measureProgress = (a) => Math.min(100, Math.round(((a._m.elapsedDays || 0) / 28) * 100))
    // "No sales added": the action's € could be MEASURED from sales but none are in,
    // so any € is only estimated. Staffing actions excluded — their € is the queue model.
    const noSalesAdded = (a) => {
      if (kindOf(a) === 'waiting') return false
      const hasSales = (a._m.readings || []).some((r) => needsSales(r.metric) && r.after != null)
        || (a._m.impact && a._m.impact.basis === 'measured')
      return !hasSales
    }

    // Cumulative € earned to date = impact/day × days elapsed. Active actions count;
    // concluded ones only if the takeaway was "worked" (so earnings don't vanish).
    const earned = computed(() => {
      let measured = 0, modelled = 0, since = null
      for (const a of actions.value) {
        const worked = a.Status === 'active' || (outcomeOf(a) && outcomeOf(a).attribution === 'worked')
        if (!worked) continue
        const imp = a._m.impact || {}
        // Days since the change, not the measured span: a concluded "worked" action keeps
        // delivering at its frozen monthly € while the change stays in the shop.
        const days = a._m.daysSincePivot ?? a._m.elapsedDays ?? 0
        const cum = (imp.eur || 0) * days / 30
        if (imp.confidence === 'good') measured += cum
        else if (imp.confidence === 'indicative') modelled += cum
        else continue
        const p = String(a._m.pivot || a.ActionDate).slice(0, 10)
        if (!since || p < since) since = p
      }
      return { measured, modelled, since, has: measured !== 0 || modelled !== 0 }
    })

    return {
      sayDay, dayShort,
      KIND_LABEL, kindOf, kindLabel, leverText, zoneShown, attrText,
      stage, openFilter, progressFilter, expanded, toggleExpanded,
      stages, openItems, openFilters, visibleOpen, suggValue, basisText, markConsidering,
      progressFilters, visibleTracked, headlineReading, measureProgress, noSalesAdded,
      levelText, salesCategoryFor,
      activeServiceSuggestions,
      loading, busy, todayIso,
      weatherHeadsUp, showWeatherHeadsUp, dismissWeatherHeadsUp,
      activeActions, concludedActions, typeChangeLine, changesToday, settingChanged, NOTICES,
      activeSuggestions, heldBackSuggestions, execFlagFor, dismissLocal, snooze, consider, startImplement, implement,
      rootEl, flashZone,
      earned,
      showAddForm, liveZones, addAction,
      saveSale, startConclude, confirmConclude, ATTRIBUTIONS,
      fmtInt, eurSigned, fmtDelta, metricLabel, chartable, needsSales, scopeLabel, outcomeOf, attrMeta,
      fmtDate, deltaClass, verdictPill, readSentence, impactView, focusReading, chartOption, rollup,
    }
  },
})
</script>

<style scoped>
.g-loading { color: #7a8a96; padding: 30px; font-style: italic; }

/* Deep-link flash: the card you arrived on from Today's "Act on this →". */
.card.sugg.flash { animation: suggFlash 2.2s ease-out; }
@keyframes suggFlash {
  0%   { box-shadow: 0 0 0 2px #4F7EB3, 0 6px 20px rgba(79,126,179,0.25); background: #eef5ff; }
  70%  { box-shadow: 0 0 0 2px #4F7EB3, 0 6px 20px rgba(79,126,179,0.18); background: #f5f9ff; }
  100% { box-shadow: none; background: transparent; }
}

/* =====================================================================
   Actions — one lifecycle stage at a time
   Calm by default: white cards on the page, one meta line, one figure, one
   primary button. Kind is a coloured dot + word (never a coloured box); detail
   opens on demand. Colours: kind = the existing lever palette; accent = brand.
   ===================================================================== */
.act {
  --ink: #1d2b3a;
  --body: #425461;
  --muted: #6b7a87;
  --faint: #93a1ad;
  --line: #e6edf3;
  --line-strong: #d3dde7;
  --accent: var(--stora-accent, #4F7EB3);
  max-width: 1100px;
  /* Responsive to ITS OWN width, not the screen's: on a tablet the sidebar stays
     open, so this area can be ~440px on an 820px screen. */
  container-type: inline-size;
  container-name: act;
}
.kind-money   { --k: #2c7a3f; }
.kind-waiting { --k: #0D9488; }
.kind-reach   { --k: #5b53a6; }
.kind-own     { --k: #7d8c99; }

/* Header */
.act-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 16px 24px;
  flex-wrap: wrap;
  margin: 4px 0 18px;
}
.act-title { margin: 0; font-size: 1.5rem; font-weight: 800; color: var(--ink); letter-spacing: -0.4px; }
.act-sub { margin: 4px 0 0; color: var(--muted); font-size: 0.92rem; }
.act-earned { display: flex; flex-direction: column; align-items: flex-end; text-align: right; }
.act-earned-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--muted);
}
.act-earned-label .bi { color: #c69a2b; }
.act-earned-value { font-size: 1.5rem; font-weight: 800; color: #2c7a3f; line-height: 1.15; letter-spacing: -0.4px; }
.act-earned-note { font-size: 0.76rem; color: var(--faint); }

/* Lifecycle tabs */
.stage-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 22px;
  border-bottom: 1px solid var(--line);
}
.btn-primary-sm.stage-bar-btn {
  margin: 4px 0;
  padding: 5px 11px;
  font-size: 0.78rem;
  gap: 5px;
}
.stage-tabs {
  display: flex;
  align-items: stretch;
  gap: 4px;
  flex-wrap: wrap;
  align-self: flex-end;
}
.stage-tab {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: -1px;
  padding: 10px 14px 12px;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  border-radius: 10px 10px 0 0;
  color: var(--muted);
  font: inherit;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.stage-tab:hover { background: #f5f8fb; color: var(--ink); }
.stage-tab.active { border-bottom-color: var(--accent); color: var(--ink); }
.stage-tab:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
.stage-count {
  min-width: 30px;
  height: 30px;
  padding: 0 9px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: #eef2f6;
  color: var(--body);
  font-size: 0.95rem;
  font-weight: 800;
}
.stage-tab.active .stage-count { background: var(--accent); color: #fff; }
.stage-text { display: flex; flex-direction: column; text-align: left; line-height: 1.2; }
.stage-label { font-size: 0.95rem; font-weight: 700; }
.stage-hint { font-size: 0.76rem; color: var(--faint); }
.stage-arrow { display: flex; align-items: center; color: #c8d1da; font-size: 0.8rem; }
@container act (max-width: 640px) {
  /* Tabs: one even row (count + name), the button full width beneath it. */
  .act .stage-bar { border-bottom: none; gap: 10px; }
  .act .stage-tabs { width: 100%; flex-wrap: nowrap; gap: 0; border-bottom: 1px solid var(--line); }
  .act .stage-hint, .act .stage-arrow { display: none; }
  .act .stage-tab { flex: 1 1 0; justify-content: center; gap: 7px; padding: 8px 4px 10px; min-width: 0; }
  .act .stage-count { min-width: 24px; height: 24px; padding: 0 7px; font-size: 0.82rem; }
  .act .stage-label { font-size: 0.86rem; white-space: nowrap; }
  .act .btn-primary-sm.stage-bar-btn { width: 100%; justify-content: center; margin: 0; padding: 8px 12px; }
  /* Filter pills: one row you swipe, never three lines. */
  .act .filters { flex-wrap: nowrap; overflow-x: auto; scrollbar-width: none; padding-bottom: 2px; width: 100%; }
  .act .filters::-webkit-scrollbar { display: none; }
  .act .filter { flex: 0 0 auto; }
  /* A soft fade at the right edge says "there's more — swipe"; the end padding lets
     the last pill scroll fully clear of it. */
  .act .filters {
    padding-right: 28px;
    -webkit-mask-image: linear-gradient(to right, #000 calc(100% - 28px), transparent);
    mask-image: linear-gradient(to right, #000 calc(100% - 28px), transparent);
  }
  /* Weather tip: text full width, "Noted" beneath it. */
  .act .notice { flex-wrap: wrap; align-items: flex-start; }
  .act .notice-body { flex: 1 1 calc(100% - 32px); }
  .act .notice .link-btn { margin-left: auto; }
  /* Header: the € figure under the title, left-aligned. */
  .act .act-earned { align-items: flex-start; text-align: left; }
}

.stage-intro { margin: 0 0 16px; max-width: 680px; color: var(--body); font-size: 0.9rem; line-height: 1.5; }
.empty-state { padding: 28px 0; color: var(--muted); font-style: italic; }

/* Weather heads-up */
.notice {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #fbf7ec;
  color: #5c4a1c;
  font-size: 0.88rem;
}
.notice-ic { font-size: 1.1rem; color: #b8892d; }
.notice-body { flex: 1; min-width: 0; line-height: 1.45; }

/* Toolbar + filter pills */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}
.filters { display: flex; flex-wrap: wrap; gap: 8px; }
.filter {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 999px;
  color: var(--body);
  font: inherit;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.filter:hover:not(:disabled) { border-color: var(--line-strong); }
.filter.active { background: var(--ink); border-color: var(--ink); color: #fff; }
.filter:disabled { opacity: 0.45; cursor: default; }
.filter:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.filter-n { color: var(--faint); font-weight: 700; }
.filter.active .filter-n { color: rgba(255, 255, 255, 0.7); }

.dot { flex: 0 0 auto; width: 8px; height: 8px; border-radius: 50%; background: var(--k, var(--faint)); }

/* One meta line, shared by every list: kind · zone · tags */
.meta { display: flex; align-items: center; flex-wrap: wrap; gap: 6px; font-size: 0.8rem; }
.meta-kind { color: var(--k); font-weight: 700; }
.meta-sep { color: #c8d1da; }
.meta-zone { color: var(--ink); font-weight: 700; }
.meta-lever { color: var(--muted); font-weight: 600; }
.meta-lever::before { content: '·'; margin-right: 6px; color: #c8d1da; }
.meta-date { margin-left: 2px; color: var(--faint); }
.meta-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 999px;
  background: #f1f4f7;
  color: var(--muted);
  font-size: 0.74rem;
  font-weight: 600;
  cursor: default;
}
.meta-tag.tag-warn { background: #fdf4e3; color: #9a6b12; }
.meta-tag.tag-nomoney { background: #fff6e5; color: #9a6b12; cursor: help; }

/* Lists */
.sugg-list, .track-list, .learned-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0;
  padding: 0;
  list-style: none;
}

/* Open: suggestion card */
.sugg {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px 28px;
  padding: 18px 20px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 14px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.sugg:hover { border-color: var(--line-strong); box-shadow: 0 4px 16px rgba(13, 38, 76, 0.06); }
.sugg.flash { animation: suggFlash 2.2s ease-out; }
.sugg-main { min-width: 0; }
.sugg-action { max-width: 66ch; margin: 8px 0 6px; color: var(--ink); font-size: 0.97rem; line-height: 1.5; }
.why-toggle {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  background: none;
  border: none;
  color: var(--accent);
  font: inherit;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
}
.sugg-why {
  max-width: 66ch;
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #f6f8fb;
  color: var(--body);
  font-size: 0.88rem;
  line-height: 1.5;
}
.sugg-why p { margin: 0; }
.sugg-why .sugg-why-note { margin-top: 6px; color: #9a6b12; }
.sugg-side { display: flex; flex-direction: column; align-items: flex-end; min-width: 150px; text-align: right; }
.value { color: var(--k); font-size: 1.4rem; font-weight: 800; line-height: 1.1; letter-spacing: -0.3px; }
.value.value-muted { font-size: 1.2rem; }
.value-unit { margin-top: 2px; color: var(--faint); font-size: 0.76rem; }
.sugg-buttons {
  grid-column: 1 / -1;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding-top: 4px;
}
.inline-form {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f6f8fb;
  color: var(--body);
  font-size: 0.86rem;
}
.inline-form label { display: flex; align-items: center; gap: 8px; font-weight: 600; }
.inline-form input, .inline-form select {
  padding: 5px 8px;
  border: 1px solid var(--line-strong);
  border-radius: 8px;
  font: inherit;
}
@container act (max-width: 680px) {
  .act .sugg { grid-template-columns: minmax(0, 1fr); gap: 6px; padding: 16px; }
  /* Flatten the text column so the figure can sit right under the zone line. */
  .act .sugg-main { display: contents; }
  .act .sugg .meta { order: 1; }
  .act .sugg-side { order: 2; flex-direction: row; align-items: baseline; gap: 8px; min-width: 0; text-align: left; }
  .act .sugg-action { order: 3; margin: 2px 0; }
  .act .why-toggle { order: 4; justify-self: start; }
  .act .sugg-why { order: 5; }
  .act .sugg-buttons { order: 6; }
  .act .inline-form { order: 7; }
  .act .value { font-size: 1.2rem; }
  .act .sugg-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding-top: 8px;
  }
  .act .sugg-buttons > :first-child, .act .sugg-buttons > :last-child { grid-column: 1 / -1; }
  .act .sugg-buttons > *, .act .sugg-buttons button { width: 100%; justify-content: center; }
  .act .inline-form { align-items: stretch; }
  .act .inline-form label { flex-wrap: wrap; width: 100%; }
}

/* Buttons */
.btn-primary-sm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  background: var(--accent);
  border: none;
  border-radius: 999px;
  color: #fff;
  font: inherit;
  font-size: 0.84rem;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: filter 0.15s ease;
}
.btn-primary-sm:hover { filter: brightness(1.08); }
.btn-primary-sm:disabled { opacity: 0.5; cursor: default; }
.btn-ghost-sm {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 999px;
  color: var(--ink);
  font: inherit;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
}
.btn-ghost-sm:hover { border-color: var(--line-strong); }
.btn-ghost-sm.on { background: #f1f4f7; border-color: var(--line-strong); }
.btn-ghost-sm.danger { color: #b3372f; }
.btn-ghost-sm.danger:hover { border-color: #e6b8b4; background: #fdf5f4; }
.icon-btn {
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 50%;
  color: var(--muted);
  cursor: pointer;
}
.icon-btn:hover { border-color: var(--line-strong); color: var(--ink); }
.link-btn {
  padding: 0 2px;
  background: none;
  border: none;
  color: var(--muted);
  font: inherit;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
}
.link-btn:hover { color: var(--ink); text-decoration: underline; }
.btn-primary-sm:focus-visible, .btn-ghost-sm:focus-visible, .icon-btn:focus-visible,
.link-btn:focus-visible, .why-toggle:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 2px;
}

/* Held back — quiet disclosure */
.held { margin-top: 18px; color: var(--muted); font-size: 0.86rem; }
/* Looks and behaves like a button: bordered pill, explicit "Show"/"Hide", chevron. */
.held-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 999px;
  color: var(--body);
  font-weight: 600;
  cursor: pointer;
  list-style: none;
  transition: border-color 0.15s ease;
}
.held-toggle::-webkit-details-marker { display: none; }
.held-toggle:hover { border-color: var(--line-strong); }
.held-toggle:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
.held-cta { display: inline-flex; align-items: center; gap: 4px; color: var(--accent); }
.held-cta .bi { transition: transform 0.15s ease; }
.held-hide { display: none; }
.held[open] .held-show { display: none; }
.held[open] .held-hide { display: inline; }
.held[open] .held-cta .bi { transform: rotate(180deg); }
.held[open] .held-toggle { margin-bottom: 6px; }
.held-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid var(--line);
}
.held-reason { margin-top: 2px; color: var(--faint); font-size: 0.8rem; }

/* In progress: tracked impact + rows */
.summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 20px;
  margin-bottom: 16px;
  padding: 14px 18px;
  background: #f4faf5;
  border: 1px solid #d9eedf;
  border-radius: 12px;
}
.summary-main { display: flex; align-items: baseline; flex-wrap: wrap; gap: 10px; }
.summary-label {
  color: var(--muted);
  font-size: 0.76rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.summary-value { color: #2c7a3f; font-size: 1.35rem; font-weight: 800; }
.summary-per { color: var(--muted); font-size: 0.85rem; font-weight: 600; }
.summary-tag { color: var(--muted); font-size: 0.76rem; cursor: help; }
.summary-extra { display: flex; flex-wrap: wrap; gap: 14px; color: var(--muted); font-size: 0.8rem; }

.track {
  overflow: hidden;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 14px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.track:hover, .track.open { border-color: var(--line-strong); box-shadow: 0 4px 16px rgba(13, 38, 76, 0.06); }
.track-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto 150px 16px;
  align-items: center;
  gap: 20px;
  width: 100%;
  padding: 14px 18px;
  background: none;
  border: none;
  font: inherit;
  text-align: left;
  cursor: pointer;
}
.track-row:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
.track-id { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
/* 6.3: an action in progress on a zone whose type changed today (calm blue) */
.track-type-note { display: block; margin-top: 4px; font-size: 12px; color: #2C5F86; }
.track-what {
  display: -webkit-box;
  overflow: hidden;
  color: var(--body);
  font-size: 0.9rem;
  line-height: 1.4;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.track-reading { display: flex; flex-direction: column; align-items: flex-end; text-align: right; }
.track-reading-label { color: var(--faint); font-size: 0.74rem; }
.track-reading-value { color: var(--ink); font-size: 0.92rem; font-weight: 700; white-space: nowrap; }
.track-reading-value.rd-up { color: #2c7a3f; }
.track-reading-value.rd-down { color: #b3372f; }
.track-reading-value.rd-pending { color: #9a6b12; font-weight: 600; }
.track-status { display: flex; flex-direction: column; align-items: flex-start; gap: 5px; }
.track-status-note { color: var(--faint); font-size: 0.74rem; }
.progress { display: block; width: 120px; height: 6px; overflow: hidden; border-radius: 999px; background: #eef2f6; }
.progress-fill { display: block; height: 100%; border-radius: 999px; background: var(--accent); }
.track-chev { color: var(--faint); }
.track-detail { padding: 2px 18px 18px; border-top: 1px solid var(--line); }
.detail-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
@container act (max-width: 760px) {
  .track-row {
    grid-template-columns: minmax(0, 1fr) auto 16px;
    grid-template-areas: "id id chev" "reading status status";
    gap: 10px 12px;
    padding: 14px 16px;
  }
  .track-id { grid-area: id; }
  .track-chev { grid-area: chev; align-self: start; }
  .track-reading { grid-area: reading; align-items: flex-start; text-align: left; }
  .track-status { grid-area: status; align-items: flex-end; text-align: right; }
  .progress { width: 96px; }
  .track-detail { padding: 2px 16px 16px; }
  .summary { padding: 12px 14px; }
  .learned { padding: 14px 16px; }
}
@container act (max-width: 420px) {
  .act .stage-tab { flex-direction: column; gap: 3px; padding: 6px 2px 8px; }
  .act .stage-label { font-size: 0.8rem; }
  .act .stage-text { align-items: center; }
  .act .sugg-buttons .btn-ghost-sm { padding: 7px 6px; font-size: 0.8rem; white-space: nowrap; }
  .act .sugg-buttons .btn-ghost-sm > .bi:not(.lock-ic) { display: none; }
  .sugg { padding: 14px; }
  .track-row { padding: 12px 14px; }
}

/* Concluded */
.learned { padding: 16px 20px; background: #fff; border: 1px solid var(--line); border-radius: 14px; }
.learned-top { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px 12px; }
.learned-chips { display: flex; gap: 6px; }
.learned-what { margin: 8px 0 4px; color: var(--ink); font-size: 0.95rem; line-height: 1.5; }
.learned-note { margin: 0 0 6px; color: var(--body); font-size: 0.88rem; font-style: italic; }
.learned-measured { margin: 0 0 6px; color: #4F7EB3; font-size: 0.8rem; }

/* Panels (log your own change) */
.panel { margin-bottom: 16px; padding: 16px 18px; border-radius: 14px; }
.panel-head { display: flex; flex-direction: column; gap: 2px; margin-bottom: 10px; }

.lead { color: #425461; font-size: 0.92rem; max-width: 780px; margin: 2px 0 16px; line-height: 1.5; }
.empty { color: #7a8a96; font-style: italic; padding: 12px 0; }
.cards { display: flex; flex-direction: column; gap: 16px; }
.card { border: 1px solid #e6edf3; border-radius: 12px; background: #fff; padding: 16px 18px; box-shadow: 0 1px 4px rgba(13,38,76,0.04); }
.card.tracked, .card.learned { max-width: 860px; }
.card.sugg { max-width: 780px; }
.card-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.titles { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.zone { font-weight: 700; color: #2b3a45; font-size: 0.98rem; }
.zone.big { font-size: 1.05rem; }
.since { color: #7a8a96; font-size: 0.82rem; }
.lever { font-size: 0.72rem; font-weight: 700; padding: 3px 9px; border-radius: 999px; text-transform: uppercase; letter-spacing: 0.03em; }
.lever-eng { background: #eef5ff; color: #4F7EB3; }
.lever-conv { background: #f3fbf5; color: #2c7a3f; }
.lever-reach { background: #f0f0fb; color: #5b53a6; }   /* same as the demo's reach chip */
/* Teal — the service family's colour in the zone-type palette. Same pale-fill,
   coloured-text treatment as its two siblings, so the row reads as one system. */
.lever-svc { background: #effaf8; color: #0D9488; }
/* Checkout Interaction's own hue, so a till suggestion is recognisable at a glance. */
.lever-chk { background: #fdf6e6; color: #a9822c; }
.eur { font-weight: 800; color: #2c7a3f; font-size: 1rem; white-space: nowrap; }
.action { color: #2b3a45; font-size: 0.94rem; line-height: 1.5; margin: 12px 0 6px; }
.why { color: #5a6b78; font-size: 0.86rem; line-height: 1.5; margin: 0 0 12px; }
.state-row { margin: 0 0 12px; }
.state-chip { font-size: 0.74rem; font-weight: 700; padding: 3px 10px; border-radius: 999px; border: 1px solid #d7e6fb; color: #4F7EB3; background: #eef5ff; }
.read { color: #2b3a45; font-size: 0.92rem; line-height: 1.55; margin: 10px 0; }
/* #3 weather caveat — a quieter, teal-tinted continuation of the read (prose, not a
   boxed chip): present only when weather may have confounded the verdict. */
.read-weather { color: #2d6a66; font-size: 0.84rem; margin-top: -2px; }
.read-weather .bi { color: #178f8f; margin-right: 3px; }

/* #2 timely weather heads-up strip — lighter than an opportunity card: teal band,
   no € / lever / lifecycle, a single "Noted" dismiss. */
.wx-headsup {
  display: flex; align-items: flex-start; gap: 12px;
  background: linear-gradient(90deg, #ecf8f7, #f5faf2);
  border: 1px solid #d9ece9; border-left: 3px solid #1f9d9d;
  border-radius: 12px; padding: 12px 14px; margin: 0 0 20px;
}
.wx-headsup-ic {
  flex: 0 0 auto; width: 34px; height: 34px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  background: #fff; color: #178f8f; font-size: 1rem;
  box-shadow: 0 1px 3px rgba(20, 60, 55, 0.08);
}
.wx-headsup-body { flex: 1 1 auto; min-width: 0; }
.wx-headsup-title { font-weight: 650; color: #12615f; font-size: 0.92rem; }
.wx-headsup-tag {
  font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px;
  color: #178f8f; background: #dff0ed; border-radius: 999px;
  padding: 2px 7px; margin-right: 8px; vertical-align: middle;
}
.wx-headsup-text { font-size: 0.84rem; color: #45605c; line-height: 1.45; margin-top: 3px; }
.wx-headsup-x {
  flex: 0 0 auto; align-self: center;
  border: 1px solid #cfe4e0; background: #fff; color: #2d6a66;
  font-size: 0.78rem; border-radius: 999px; padding: 4px 12px; cursor: pointer;
}
.wx-headsup-x:hover { background: #eef7f5; }
@media (max-width: 640px) {
  .wx-headsup { flex-wrap: wrap; }
  .wx-headsup-x { align-self: flex-start; }
}
.read :deep(strong) { color: #2b3a45; }

.btn-row { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 4px; }
.btn { appearance: none; border: 1px solid transparent; border-radius: 8px; padding: 7px 12px; font-size: 0.82rem; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary { background: #4F7EB3; color: #fff; }
.btn-primary:hover { background: #416a99; }
.btn-soft { background: #f4f7fb; color: #425461; border-color: #e6edf3; }
.btn-ghost { background: transparent; color: #7a8a96; }
.btn-danger-ghost { background: transparent; color: #b0464b; border-color: #f0dcdd; }
.btn-add { appearance: none; border: 1px solid #cdd6df; background: #f5f7fa; color: #4F7EB3; font-size: 0.82rem; font-weight: 600; border-radius: 8px; padding: 6px 12px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }

.mini-form, .mini-actions { display: flex; gap: 8px; align-items: flex-end; flex-wrap: wrap; }
.mini-form { background: #f7fbff; border: 1px solid #e2ecf7; border-radius: 10px; padding: 12px 14px; margin-top: 4px; }
.mini-form label { display: flex; flex-direction: column; gap: 5px; font-size: 0.78rem; font-weight: 700; color: #425461; }
.mini-form input, .fld input, .fld select, .fld textarea { border: 1px solid #d7e2ee; border-radius: 7px; padding: 6px 8px; font-size: 0.85rem; color: #2b3a45; background: #fff; font-family: inherit; }

.add-form, .sales-inline, .conclude-panel { border: 1px solid #e2ecf7; background: #f9fcff; border-radius: 10px; padding: 14px 16px; margin: 8px 0 14px; }
.add-head { display: flex; align-items: baseline; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.add-head strong { color: #2b3a45; font-size: 0.98rem; }
.add-note { color: #7a8a96; font-size: 0.78rem; font-style: italic; }
.fld { display: flex; flex-direction: column; gap: 5px; font-size: 0.78rem; font-weight: 700; color: #425461; margin-bottom: 12px; }
.fld textarea { resize: vertical; font-weight: 400; }
.fld-row { display: flex; gap: 14px; flex-wrap: wrap; }
.fld-row .fld { flex: 1 1 200px; }
.metric-picks { display: flex; gap: 18px; flex-wrap: wrap; font-weight: 400; }
.pick { display: inline-flex; align-items: center; gap: 6px; font-size: 0.85rem; color: #2b3a45; font-weight: 400; }

.rollup { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px 18px; background: #f3fbf5; border: 1px solid #d9f1df; border-radius: 12px; padding: 14px 18px; margin: 0 0 18px; max-width: 860px; }
.rollup-main { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.rollup-label { color: #2c7a3f; font-weight: 700; font-size: 0.92rem; }
.rollup-eur { color: #2c7a3f; font-weight: 800; font-size: 1.4rem; line-height: 1; }
.rollup-per { font-size: 0.9rem; font-weight: 700; opacity: 0.8; }
.rollup-tag { font-size: 0.74rem; font-weight: 700; color: #2c7a3f; display: inline-flex; align-items: center; gap: 4px; background: #e4f6e9; border-radius: 999px; padding: 2px 9px; }
.rollup-extra { display: flex; gap: 8px; flex-wrap: wrap; }
.rollup-chip { font-size: 0.78rem; font-weight: 600; border-radius: 999px; padding: 4px 10px; }
.chip-modelled { color: #35618f; background: #eef5ff; border: 1px solid #d7e6fb; }
.chip-muted { color: #7a8a96; background: #fff; border: 1px solid #e6edf3; }

.impact { display: flex; align-items: center; gap: 9px; flex-wrap: wrap; font-size: 0.92rem; padding: 9px 12px; border-radius: 9px; margin: 4px 0 12px; border: 1px solid transparent; }
.imp-text b { font-weight: 800; }
.imp-basis { font-size: 0.76rem; font-weight: 600; opacity: 0.8; display: inline-flex; align-items: center; gap: 4px; }
.imp-good { background: #f3fbf5; border-color: #d9f1df; color: #2c7a3f; }
.imp-indicative { background: #eef5ff; border-color: #d7e6fb; color: #35618f; }
.imp-early { background: #fff7e6; border-color: #ffe7bf; color: #b66a00; }
.imp-bad { background: #fdf3f3; border-color: #f0dcdd; color: #b0464b; }
.imp-none { background: #f7f9fb; border-color: #eef2f6; color: #7a8a96; }
.imp-na { background: #f7f9fb; border: 1px dashed #dbe3ec; color: #8a97a3; }

.delta-row { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 8px; }
.delta-chip { appearance: none; font-family: inherit; font-size: 0.78rem; color: #5a6b78; padding: 4px 10px; border-radius: 999px; border: 1px solid #e6edf3; background: #fbfdff; cursor: pointer; }
.delta-chip strong { margin-left: 4px; }
.delta-chip.up { color: #2c7a3f; border-color: #d9f1df; background: #f5fbf7; }
.delta-chip.down { color: #b0464b; border-color: #f0dcdd; background: #fdf6f6; }
.delta-chip.chip-active { box-shadow: 0 0 0 2px rgba(79,126,179,0.28); border-color: #4F7EB3; }
.delta-chip.chip-pending { color: #4F7EB3; border-style: dashed; border-color: #b9cde3; background: #f5f9fe; }
.pending-tag { margin-left: 6px; font-weight: 700; }

.chart { width: 100%; height: 240px; margin-top: 6px; }
.chart-caption { color: #7a8a96; font-size: 0.78rem; margin: 2px 0 12px; }

.retry-flag { display: inline-flex; align-items: center; gap: 6px; font-size: 0.74rem; font-weight: 700; color: #b66a00; background: #fff7e6; border: 1px solid #ffe7bf; border-radius: 999px; padding: 3px 10px; margin: 4px 0 2px; }

.held-block { margin-top: 18px; border-top: 1px dashed #e6edf3; padding-top: 14px; max-width: 780px; }
.held-head { display: flex; align-items: center; gap: 8px; font-size: 0.82rem; font-weight: 700; color: #9aa8b3; margin-bottom: 10px; }
.held-list { display: flex; flex-direction: column; gap: 8px; }
.held-row { display: flex; align-items: center; justify-content: space-between; gap: 14px; background: #f7f9fb; border: 1px solid #eef2f6; border-radius: 10px; padding: 10px 14px; opacity: 0.85; }
.held-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.held-action { color: #6c7a86; font-size: 0.86rem; text-decoration: line-through; text-decoration-color: #c7d3df; }
.held-reason { color: #9aa8b3; font-size: 0.78rem; font-style: italic; }

.attr-list { display: flex; flex-direction: column; gap: 8px; margin: 12px 0; }
.attr-opt { display: flex; align-items: center; gap: 9px; font-size: 0.88rem; color: #2b3a45; border: 1px solid #e6edf3; border-radius: 8px; padding: 9px 12px; cursor: pointer; background: #fff; }
.attr-opt.attr-sel { border-color: #4F7EB3; background: #f2f7fd; }
.attr-chip { font-size: 0.72rem; font-weight: 800; padding: 3px 10px; border-radius: 999px; }
.attr-good { background: #f3fbf5; color: #2c7a3f; }
.attr-warn { background: #fff7e6; color: #b66a00; }
.attr-bad { background: #fdf3f3; color: #b0464b; }
.attr-flat { background: #f2f5f8; color: #6c7a86; }
.allen-line { color: #4F7EB3; font-size: 0.85rem; margin: 10px 0 2px; display: flex; align-items: baseline; gap: 7px; }

.earned { margin-top: 28px; max-width: 860px; }
.earned-inner { background: #1a2a36; color: #fff; border-radius: 14px; padding: 18px 22px; display: flex; flex-direction: column; gap: 8px; }
.earned-label { font-size: 0.82rem; font-weight: 700; color: #c3d16a; display: flex; align-items: center; gap: 8px; }
.earned-main { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.earned-eur { font-size: 2rem; font-weight: 800; color: #fff; line-height: 1; }
.earned-tag { font-size: 0.8rem; opacity: 0.75; }
.earned-extra { display: flex; gap: 8px; }
</style>
